import { apiService } from './api/api';
import { tokenManager } from './api/tokenManager';
import { User, CreateUserData, UpdateUserData } from '../types/user';
import { API_ENDPOINTS, STORAGE_KEYS } from '../constants/api';

export interface LoginRequest {
  email: string;
  password: string;
}

export type SignupRequest = CreateUserData;

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

export class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials);
    
    this.saveAuthData(response);
    
    return response;
  }

  async signup(userData: SignupRequest): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.SIGNUP, userData);
    
    this.saveAuthData(response);
    
    return response;
  }

  async logout(): Promise<void> {
    const refreshToken = tokenManager.getRefreshToken();
    try {
      await apiService.post(API_ENDPOINTS.AUTH.LOGOUT, { refreshToken });
    } finally {
      tokenManager.clearTokens();
    }
  }

  async getCurrentUser(): Promise<User> {
    return apiService.get<User>(API_ENDPOINTS.AUTH.CURRENT_USER);
  }

  async updateUserProfile(data: UpdateUserData): Promise<User> {   
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    const { id } = user;
    const response = await apiService.put<User>(`${API_ENDPOINTS.USER.UPDATE}/${id}`, data);

    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response));
    
    return response;
  }

  async verifyToken(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  private saveAuthData(authResponse: AuthResponse): void {
    tokenManager.setTokens({
      accessToken: authResponse.accessToken,
      refreshToken: authResponse.refreshToken,
      expiresAt: authResponse.expiresIn 
        ? Date.now() + (authResponse.expiresIn * 1000) 
        : undefined
    });

    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(authResponse.user));
  }
}

export const authService = new AuthService();
